import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { auth } from '../api';

const VerifyEmailPage = ({ setCurrentPage }) => {
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const location = useLocation();
  const [token, setToken] = useState('');
  
  // Extract token on component mount
  useEffect(() => {
    console.log("Current location:", window.location);
    
    // Handling hash-based URLs (#/verify-email?token=...)
    const hashParams = new URLSearchParams(window.location.hash.includes('?') 
      ? window.location.hash.substring(window.location.hash.indexOf('?'))
      : '');
    const tokenFromHash = hashParams.get('token');
    
    // Handling query params in regular URLs (/verify-email?token=...)
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    
    const finalToken = tokenFromHash || tokenFromUrl;
    
    if (finalToken) {
      setToken(finalToken);
      console.log("Token extracted:", finalToken);
    } else {
      setStatus('error');
      setMessage('No verification token found in the URL.');
    }
  }, [location.search, location.hash]);
  
  // Verify email when token is available
  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
  }, [token]);
  
  const verifyEmail = async (verificationToken) => {
    try {
      console.log("Attempting to verify email with token:", verificationToken);
      
      // Make sure the token doesn't contain any extra URL parameters
      const cleanToken = verificationToken.split('&')[0];
      console.log("Clean token for verification:", cleanToken);
      
      // Call the API to verify the token
      const response = await auth.verifyEmail(cleanToken);
      console.log("Verification API response:", response);
      
      // If successful, store the token in localStorage
      localStorage.setItem('token', response.data.access_token);
      
      // Store user info in localStorage as well
      const userInfo = {
        id: response.data.user_id,
        email: response.data.email,
        is_admin: response.data.is_admin,
        token: response.data.access_token
      };
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      setStatus('success');
      setMessage('Your email has been successfully verified!');
      
      // Force a window reload to ensure all app state is refreshed
      setTimeout(() => {
        // Clear the URL params
        window.history.replaceState({}, document.title, '/');
        
        // Force a full page reload to ensure the app recognizes the login state
        window.location.href = '/';
      }, 2000);
      
    } catch (error) {
      console.error('Verification error:', error);
      console.error('Error response:', error.response?.data);
      
      setStatus('error');
      setMessage(
        error.response?.data?.detail || 
        'Failed to verify your email. The link may have expired or is invalid.'
      );
    }
  };
  
  const resendVerification = async () => {
    try {
      // Extract email from the URL if possible
      const params = new URLSearchParams(location.search);
      const email = params.get('email');
      
      if (!email) {
        setMessage('Please enter your email address to resend verification.');
        return;
      }
      
      await auth.resendVerification(email);
      setMessage('Verification email resent! Please check your inbox.');
    } catch (error) {
      console.error('Error resending verification:', error);
      setMessage('Failed to resend verification email. Please try again.');
    }
  };
  
  const goToLogin = () => {
    // Clear the URL params
    window.history.replaceState({}, document.title, '/');
    setCurrentPage('auth');
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Email Verification
        </h2>
      </div>
      
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow rounded-lg sm:px-10">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700 mx-auto"></div>
              <p className="mt-4 text-gray-600">Verifying your email address...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="text-center">
              <div className="rounded-full h-12 w-12 bg-green-100 flex items-center justify-center mx-auto">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <p className="mt-4 text-gray-600">{message}</p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center">
              <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <p className="mt-4 text-gray-600">{message}</p>
              <button
                onClick={resendVerification}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
              >
                Resend Verification Email
              </button>
              <button 
                onClick={goToLogin} 
                className="mt-4 text-sm text-emerald-600 hover:text-emerald-500"
              >
                Return to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
